import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, ShieldCheck, ArrowSquareOut, CheckCircle,
  WarningCircle, Sparkle, MagnifyingGlass,
} from "@phosphor-icons/react";

const REASON_LABEL = {
  never_verified: "Never verified",
  stale_over_30_days: "Stale > 30 days",
  no_funder_website: "No funder URL",
};

const REASON_COLOR = {
  never_verified: "bg-rose-500/10 border-rose-500/40 text-rose-300",
  stale_over_30_days: "bg-amber-500/10 border-amber-500/40 text-amber-300",
  no_funder_website: "bg-zinc-500/10 border-zinc-500/40 text-zinc-300",
};

const formatStaleness = (days) => {
  if (days >= 9999) return "—";
  if (days === 0) return "today";
  if (days === 1) return "1d";
  return `${days}d`;
};

const AdminVerificationQueue = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [verifying, setVerifying] = useState(null);

  // Fact-check state
  const [factCheckDraft, setFactCheckDraft] = useState("");
  const [factChecking, setFactChecking] = useState(false);
  const [factCheckResult, setFactCheckResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/grants/verification-queue`);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markVerified = async (grantId) => {
    setVerifying(grantId);
    try {
      await axios.post(`${API}/admin/grants/${grantId}/verify`);
      toast.success("Marked verified");
      setItems((prev) => prev.filter((i) => i.grant_id !== grantId));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark verified");
    } finally {
      setVerifying(null);
    }
  };

  const runFactCheck = async () => {
    const draft = factCheckDraft.trim();
    if (!draft) {
      toast.error("Paste a draft first");
      return;
    }
    setFactChecking(true);
    setFactCheckResult(null);
    try {
      const res = await axios.post(`${API}/admin/fact-check`, { draft });
      setFactCheckResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fact-check failed");
    } finally {
      setFactChecking(false);
    }
  };

  const filtered = items.filter((i) => {
    if (filter !== "all" && i.reason !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (i.title || "").toLowerCase().includes(q) ||
        (i.funder_name || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const reasonCounts = items.reduce((acc, i) => {
    acc[i.reason] = (acc[i.reason] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="admin-verification-queue-page">
      <header className="border-b border-zinc-800/60 bg-zinc-950/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-400 text-sm" data-testid="back-to-admin-link">
            <ArrowLeft className="w-4 h-4" />
            Back to admin
          </Link>
          <Link to="/admin/funder-status" className="text-xs text-zinc-500 hover:text-emerald-400">
            See auto-crawl report →
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full mb-3 text-[10px] font-bold uppercase tracking-[0.18em] bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
            <ShieldCheck weight="fill" className="w-3 h-3" />
            Verification Queue
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" data-testid="page-title">
            Grants that need your eyes
          </h1>
          <p className="text-sm text-zinc-400 max-w-2xl">
            The weekly auto-crawl verifies most active grants for you. Only the ones the crawler couldn&apos;t confidently verify — 403 anti-bot blocks, missing funder URLs, never-verified seeds — land here. Manually verifying clears them off your plate.
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="In queue" value={total} accent={total > 0 ? "amber" : "emerald"} testId="stat-total" />
          <StatCard label="Never verified" value={reasonCounts.never_verified || 0} testId="stat-never" />
          <StatCard label="Stale > 30d" value={reasonCounts.stale_over_30_days || 0} testId="stat-stale" />
          <StatCard label="No funder URL" value={reasonCounts.no_funder_website || 0} testId="stat-no-url" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {["all", "never_verified", "stale_over_30_days", "no_funder_website"].map((f) => {
            const count = f === "all" ? items.length : (reasonCounts[f] || 0);
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${
                  filter === f
                    ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200"
                    : "bg-zinc-950/40 border-zinc-800 text-zinc-400 hover:border-zinc-600"
                }`}
                data-testid={`filter-${f}`}
              >
                {f === "all" ? "All" : REASON_LABEL[f]} · {count}
              </button>
            );
          })}
          <div className="relative ml-auto">
            <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title or funder…"
              className="pl-8 pr-3 py-1.5 rounded-sm text-xs bg-zinc-950/40 border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500/60 w-56"
              data-testid="queue-search"
            />
          </div>
        </div>

        {/* List */}
        {loading && (
          <p className="text-sm text-zinc-500" data-testid="queue-loading">Loading queue…</p>
        )}

        {!loading && filtered.length === 0 && (
          <Card className="bg-emerald-500/5 border-emerald-500/30">
            <CardContent className="p-6 flex items-start gap-3" data-testid="queue-empty">
              <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-emerald-300">Inbox zero.</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Every active grant is verified. The next weekly crawl runs automatically — come back here when something needs review.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-2" data-testid="queue-list">
            {filtered.map((g) => (
              <Card key={g.grant_id} className="bg-zinc-950/60 border-zinc-800 hover:border-zinc-700 transition-colors">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${REASON_COLOR[g.reason] || REASON_COLOR.never_verified}`}
                          data-testid={`reason-${g.grant_id}`}
                        >
                          {REASON_LABEL[g.reason] || g.reason}
                        </span>
                        {g.deadline && (
                          <span className="text-[10px] text-zinc-500">deadline: {g.deadline}</span>
                        )}
                        {g.category && (
                          <span className="text-[10px] text-zinc-500">· {g.category}</span>
                        )}
                      </div>
                      <p className="text-sm font-bold text-white" data-testid={`title-${g.grant_id}`}>{g.title}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{g.funder_name || "—"}</p>
                      {g.funder_website ? (
                        <a
                          href={g.funder_website.startsWith("http") ? g.funder_website : `https://${g.funder_website}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 mt-2 font-mono"
                          data-testid={`open-site-${g.grant_id}`}
                        >
                          {g.funder_website.slice(0, 70)}{g.funder_website.length > 70 ? "…" : ""}
                          <ArrowSquareOut className="w-3 h-3" />
                        </a>
                      ) : (
                        <p className="text-xs text-rose-400 mt-2 italic">No funder website on file — add one in /admin/grants before this can auto-verify.</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">staleness</span>
                      <span className="text-base font-black text-zinc-300">{formatStaleness(g.staleness_days)}</span>
                      <Button
                        onClick={() => markVerified(g.grant_id)}
                        disabled={verifying === g.grant_id}
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-400 text-black mt-1"
                        data-testid={`verify-${g.grant_id}`}
                      >
                        {verifying === g.grant_id ? "Marking…" : "Mark verified"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Fact-check section */}
        <section className="mt-16 pt-10 border-t border-zinc-800/60" data-testid="factcheck-section">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full mb-3 text-[10px] font-bold uppercase tracking-[0.18em] bg-violet-500/15 border border-violet-500/40 text-violet-300">
              <Sparkle weight="fill" className="w-3 h-3" />
              Fact-check before you send
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-2">Paste anything outbound</h2>
            <p className="text-sm text-zinc-400 max-w-2xl">
              Drafts of Sunday Drops, LinkedIn posts, cold emails, sponsor pitches. Every factual claim gets labeled <span className="text-emerald-300 font-semibold">verified</span>, <span className="text-amber-300 font-semibold">needs verification</span>, or <span className="text-zinc-300 font-semibold">opinion</span>. Cross-checked against <code className="text-[11px] bg-zinc-900 px-1 py-0.5 rounded">verified_facts.md</code>.
            </p>
          </div>

          <textarea
            value={factCheckDraft}
            onChange={(e) => setFactCheckDraft(e.target.value)}
            placeholder="Paste the draft email / LinkedIn post / Sunday Drop copy here…"
            rows={8}
            className="w-full px-4 py-3 rounded-sm bg-zinc-950/60 border border-zinc-800 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500/60 font-mono resize-y"
            data-testid="factcheck-draft-input"
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-zinc-500">{factCheckDraft.length} / 20000 chars</p>
            <Button
              onClick={runFactCheck}
              disabled={factChecking || !factCheckDraft.trim()}
              className="bg-violet-500 hover:bg-violet-400 text-black"
              data-testid="factcheck-run-btn"
            >
              {factChecking ? "Checking…" : "Fact-check this"}
            </Button>
          </div>

          {factCheckResult && (
            <div className="mt-6 space-y-4" data-testid="factcheck-result">
              <Card className="bg-zinc-950/60 border-zinc-800">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Summary</p>
                  <p className="text-sm text-zinc-200">{factCheckResult.summary}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
                    <StatCard small label="Verified" value={factCheckResult.counts?.verified || 0} accent="emerald" testId="fc-verified" />
                    <StatCard small label="Need verify" value={factCheckResult.counts?.needs_external_verification || 0} accent="amber" testId="fc-needs" />
                    <StatCard small label="Opinion" value={factCheckResult.counts?.opinion || 0} testId="fc-opinion" />
                    <StatCard small label="High-sev unverified" value={factCheckResult.counts?.high_severity_unverified || 0} accent={(factCheckResult.counts?.high_severity_unverified || 0) > 0 ? "rose" : "emerald"} testId="fc-high" />
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2" data-testid="factcheck-claims-list">
                {(factCheckResult.claims || []).map((c, idx) => {
                  const isVerified = c.category === "verified";
                  const isOpinion = c.category === "opinion";
                  const isHigh = c.severity === "high";
                  const tone = isVerified
                    ? "border-emerald-500/40 bg-emerald-500/5"
                    : isOpinion
                    ? "border-zinc-700 bg-zinc-950/40"
                    : isHigh
                    ? "border-rose-500/50 bg-rose-500/5"
                    : "border-amber-500/40 bg-amber-500/5";
                  return (
                    <Card key={idx} className={`${tone} border`}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2">
                          {isVerified ? (
                            <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                          ) : isOpinion ? (
                            <span className="w-4 h-4 mt-0.5 shrink-0 text-zinc-500 text-center text-[10px] font-bold">i</span>
                          ) : (
                            <WarningCircle weight="fill" className={`w-4 h-4 mt-0.5 shrink-0 ${isHigh ? "text-rose-400" : "text-amber-400"}`} />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-zinc-200 italic">&ldquo;{c.claim}&rdquo;</p>
                            {c.source && (
                              <p className="text-[11px] text-zinc-500 mt-1">→ {c.source}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-0.5 shrink-0">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{c.category?.replace(/_/g, " ")}</span>
                            {!isOpinion && !isVerified && (
                              <span className={`text-[9px] uppercase font-bold ${isHigh ? "text-rose-400" : "text-amber-400"}`}>{c.severity}</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent = "zinc", testId, small = false }) => {
  const colorMap = {
    zinc: "text-zinc-200 border-zinc-800",
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
    amber: "text-amber-300 border-amber-500/40 bg-amber-500/5",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/5",
  };
  return (
    <div
      className={`rounded-sm border px-3 ${small ? "py-2" : "py-3"} bg-zinc-950/40 ${colorMap[accent] || colorMap.zinc}`}
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
      <p className={`${small ? "text-lg" : "text-2xl"} font-black tracking-tight`}>{value ?? "—"}</p>
    </div>
  );
};

export default AdminVerificationQueue;
